import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { ItemStatusCode } from '@/features/cart/types';
import type { ItemStatusCode as ItemStatusCodeType } from '@/features/cart/types';

interface CartItemStatusTestContextValue {
  getTestItemStatus: (cartItemId: string) => ItemStatusCodeType | null;
  setTestItemStatus: (cartItemId: string, status: ItemStatusCodeType | null) => void;
  globalTestItemStatus: ItemStatusCodeType | null;
  setGlobalTestItemStatus: (status: ItemStatusCodeType | null) => void;
  getTestAddOnStatus: (addOnId: string) => ItemStatusCodeType | null;
  setTestAddOnStatus: (addOnId: string, status: ItemStatusCodeType | null) => void;
  globalTestAddOnStatus: ItemStatusCodeType | null;
  setGlobalTestAddOnStatus: (status: ItemStatusCodeType | null) => void;
}

const CartItemStatusTestContext = createContext<CartItemStatusTestContextValue | null>(null);

export const useCartItemStatusTest = () => {
  const context = useContext(CartItemStatusTestContext);
  if (!context) {
    // Return no-op functions if not in context (production or no provider)
    return {
      getTestItemStatus: () => null,
      setTestItemStatus: () => {},
      globalTestItemStatus: null,
      setGlobalTestItemStatus: () => {},
      getTestAddOnStatus: () => null,
      setTestAddOnStatus: () => {},
      globalTestAddOnStatus: null,
      setGlobalTestAddOnStatus: () => {},
    };
  }
  return context;
};

interface CartItemStatusTestProviderProps {
  children: React.ReactNode;
}

/**
 * Provider for testing cart item status UI in development.
 * Wraps the app to enable status overrides for any cart item.
 */
export const CartItemStatusTestProvider: React.FC<CartItemStatusTestProviderProps> = ({ children }) => {
  const [itemStatusOverrides, setItemStatusOverrides] = useState<Record<string, ItemStatusCodeType>>({});
  const [globalTestItemStatus, setGlobalTestItemStatus] = useState<ItemStatusCodeType | null>(null);
  const [addOnStatusOverrides, setAddOnStatusOverrides] = useState<Record<string, ItemStatusCodeType>>({});
  const [globalTestAddOnStatus, setGlobalTestAddOnStatus] = useState<ItemStatusCodeType | null>(null);

  const getTestItemStatus = useCallback((cartItemId: string): ItemStatusCodeType | null => {
    if (globalTestItemStatus) {
      return globalTestItemStatus;
    }
    return itemStatusOverrides[cartItemId] || null;
  }, [itemStatusOverrides, globalTestItemStatus]);

  const setTestItemStatus = useCallback((cartItemId: string, status: ItemStatusCodeType | null) => {
    setItemStatusOverrides(prev => {
      if (status === null) {
        const { [cartItemId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [cartItemId]: status };
    });
  }, []);

  const getTestAddOnStatus = useCallback((addOnId: string): ItemStatusCodeType | null => {
    if (globalTestAddOnStatus) {
      return globalTestAddOnStatus;
    }
    return addOnStatusOverrides[addOnId] || null;
  }, [addOnStatusOverrides, globalTestAddOnStatus]);

  const setTestAddOnStatus = useCallback((addOnId: string, status: ItemStatusCodeType | null) => {
    setAddOnStatusOverrides(prev => {
      if (status === null) {
        const { [addOnId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [addOnId]: status };
    });
  }, []);

  // Only provide context in development
  if (import.meta.env.PROD) {
    return <>{children}</>;
  }

  return (
    <CartItemStatusTestContext.Provider value={{
      getTestItemStatus,
      setTestItemStatus,
      globalTestItemStatus,
      setGlobalTestItemStatus,
      getTestAddOnStatus,
      setTestAddOnStatus,
      globalTestAddOnStatus,
      setGlobalTestAddOnStatus,
    }}>
      {children}
    </CartItemStatusTestContext.Provider>
  );
};

// All item statuses for the switcher
const allItemStatuses: { value: ItemStatusCodeType | ''; label: string }[] = [
  { value: '', label: '-- Use API Status --' },
  // General Statuses
  { value: ItemStatusCode.NoStatus, label: '❓ No Status' },
  { value: ItemStatusCode.GoodForBooking, label: '✅ Good for Booking' },
  { value: ItemStatusCode.PriceUpdateRequired, label: '💰 Price Update Required' },
  { value: ItemStatusCode.GoodButAddOnIssues, label: '⚠️ Good but Add-on Issues' },
  // Event Booking Category Statuses
  { value: ItemStatusCode.EBCCancelled, label: '❌ Event Cancelled' },
  { value: ItemStatusCode.EBCInPast, label: '⏰ Event in Past' },
  { value: ItemStatusCode.EBCUnavailable, label: '🚫 Event Unavailable' },
  { value: ItemStatusCode.EBCSoldOut, label: '📛 Event Sold Out' },
  { value: ItemStatusCode.EBCInsufficientCapacity, label: '👥 Insufficient Capacity' },
  { value: ItemStatusCode.TableGone, label: '🪑 Table Gone' },
  { value: ItemStatusCode.TableInsufficientCapacity, label: '🪑 Table Insufficient Capacity' },
  { value: ItemStatusCode.CurrencyMismatch, label: '💱 Currency Mismatch' },
];

// All add-on statuses for the switcher
const allAddOnStatuses: { value: ItemStatusCodeType | ''; label: string }[] = [
  { value: '', label: '-- Use API Status --' },
  { value: ItemStatusCode.AddOnGoodForBooking, label: '✅ Add-on Good' },
  { value: ItemStatusCode.AddOnDisabled, label: '🚫 Add-on Disabled' },
  { value: ItemStatusCode.AddOnUnavailable, label: '❌ Add-on Unavailable' },
  { value: ItemStatusCode.AddOnInsufficientInventory, label: '📦 Insufficient Inventory' },
];

/**
 * Floating cart item status switcher for testing all item statuses globally.
 * Only renders in development mode.
 */
export const GlobalCartItemStatusSwitcher: React.FC = () => {
  const { 
    globalTestItemStatus, 
    setGlobalTestItemStatus,
    globalTestAddOnStatus,
    setGlobalTestAddOnStatus,
  } = useCartItemStatusTest();

  // Only render in development
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <motion.div
      className="fixed bottom-4 left-4 z-[9999] p-3 rounded-lg shadow-xl"
      style={{
        background: 'rgba(0, 0, 0, 0.95)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(59, 130, 246, 0.5)',
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-xs text-blue-400 font-semibold mb-2 uppercase tracking-wider">
        🛒 Cart Status Tester
      </div>
      
      {/* Item Status Switcher */}
      <div className="mb-2">
        <label className="text-xs text-gray-500 mb-1 block">Item Status</label>
        <select
          value={globalTestItemStatus || ''}
          onChange={(e) => setGlobalTestItemStatus((e.target.value || null) as ItemStatusCodeType | null)}
          className="w-full px-3 py-2 rounded-md text-sm font-medium cursor-pointer min-w-[200px]"
          style={{
            background: 'rgba(31, 41, 55, 0.9)',
            color: '#fff',
            border: '1px solid rgba(75, 85, 99, 0.5)',
            outline: 'none',
          }}
        >
          {allItemStatuses.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>
      
      {/* Add-On Status Switcher */}
      <div>
        <label className="text-xs text-gray-500 mb-1 block">Add-on Status</label>
        <select
          value={globalTestAddOnStatus || ''}
          onChange={(e) => setGlobalTestAddOnStatus((e.target.value || null) as ItemStatusCodeType | null)}
          className="w-full px-3 py-2 rounded-md text-sm font-medium cursor-pointer min-w-[200px]"
          style={{
            background: 'rgba(31, 41, 55, 0.9)',
            color: '#fff',
            border: '1px solid rgba(75, 85, 99, 0.5)',
            outline: 'none',
          }}
        >
          {allAddOnStatuses.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>
      
      {(globalTestItemStatus || globalTestAddOnStatus) && (
        <div className="text-xs text-amber-400 mt-2 space-y-1">
          {globalTestItemStatus && <div>Item: {globalTestItemStatus}</div>}
          {globalTestAddOnStatus && <div>Add-on: {globalTestAddOnStatus}</div>}
        </div>
      )}
    </motion.div>
  );
};
